import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus, X, CheckCircle2, Mail, FileQuestion, MousePointerClick, HelpCircle, ChevronDown } from 'lucide-react';
import EditRequestForm from '../../components/dashboard/EditRequestForm';
import EditRequestList from '../../components/dashboard/EditRequestList';
import ContentEditsWalkthrough from '../../components/dashboard/ContentEditsWalkthrough';
import InlineUndoButton from '../../components/portal/inline-edit/InlineUndoButton';
import WebsiteUpdateRecord, { WebsiteUpdateStatusChip } from '../../components/portal/inline-edit/WebsiteUpdateRecord';
import {
  formatWhen, parseServerDate, pagesText, pluralize, summarizeWebsiteUpdate,
} from '../../components/portal/inline-edit/inlineEdit';
import { api } from '../../lib/api';
import { usePortalScope } from '../../context/PortalScopeContext';
import { VISUAL_EDITOR_ENABLED } from '../../config/featureFlags';

const WALKTHROUGH_KEY = 'nexsav_content_edits_walkthrough_seen';

export default function DashboardEdits() {
  const { account } = usePortalScope();
  const navigate = useNavigate();
  const location = useLocation();
  const [editRequests, setEditRequests] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // The Visual Editor's "Send as an edit request instead" opens the form.
  const [formOpen, setFormOpen] = useState(() => location.state?.openForm === true);
  const [justSubmitted, setJustSubmitted] = useState(null);
  const [showWalkthrough, setShowWalkthrough] = useState(() => {
    try { return localStorage.getItem(WALKTHROUGH_KEY) !== '1'; } catch { return false; }
  });

  // Sprint J: synthesized admin-preview path collapsed; everything routes
  // through /portal now (impersonation puts us in a real client session).
  const visualPath = '/portal/edits/visual';
  const visualAvailable = VISUAL_EDITOR_ENABLED && !!account?.siteUrl;

  const closeWalkthrough = () => {
    setShowWalkthrough(false);
    try { localStorage.setItem(WALKTHROUGH_KEY, '1'); } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [editReqData, pagesData] = await Promise.all([
        api.get('/api/edit-requests'),
        api.get(`/api/pages${account?.id ? `?account=${account.id}` : ''}`),
      ]);
      setEditRequests(editReqData.editRequests || []);
      setViewer(editReqData.viewer || null);
      setPages(pagesData.pages || []);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load edit requests');
    } finally {
      setLoading(false);
    }
  }, [account?.id]);

  const handleDelete = useCallback(async (id) => {
    try {
      await api.delete(`/api/edit-requests/${id}`);
      setEditRequests((list) => list.filter((r) => r.id !== id));
    } catch (err) {
      setLoadError(err?.message || 'Failed to delete request');
    }
  }, []);

  const handleEdit = useCallback(async (id, fields) => {
    try {
      const data = await api.put(`/api/edit-requests/${id}`, fields);
      setEditRequests((list) => list.map((r) => (r.id === id ? { ...r, ...data.editRequest } : r)));
      return true;
    } catch (err) {
      setLoadError(err?.message || 'Failed to update request');
      return false;
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onCreated = (editRequest) => {
    setEditRequests((prev) => [editRequest, ...prev]);
    setFormOpen(false);
    setJustSubmitted(editRequest);
    setTimeout(() => setJustSubmitted(null), 5000);
  };

  const hasPages = pages.length > 0;
  // Publishes from the Visual Editor ("website updates") have their own
  // section, newest first; the list below keeps the requests people wrote.
  const websiteUpdates = useMemo(() => editRequests
    .filter((r) => r.source === 'inline')
    .sort((a, b) => (parseServerDate(b.createdAt)?.getTime() || 0) - (parseServerDate(a.createdAt)?.getTime() || 0)),
  [editRequests]);
  const formRequests = useMemo(() => editRequests.filter((r) => r.source !== 'inline'), [editRequests]);
  const siteOrigin = useMemo(() => {
    try { return account?.siteUrl ? new URL(account.siteUrl).origin : null; } catch { return null; }
  }, [account?.siteUrl]);
  // (canRevert stays as loaded so the Undo control can keep showing its result.)
  const onReverted = useCallback((er) => {
    setEditRequests((list) => list.map((r) => (r.id === er?.id
      ? { ...r, status: er.status, publishState: er.publishState, revertedAt: er.revertedAt }
      : r)));
  }, []);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Content <span className="serif-acc accent-text">Edits</span></h1>
          <p className="text-sm text-gray-500 mt-1">
            Request updates to your website's content. Describe a change, or point at it visually.
          </p>
          <button
            type="button"
            data-tour="how-it-works"
            onClick={() => setShowWalkthrough(true)}
            className="inline-flex items-center gap-1.5 mt-2 text-[12px] text-[#B0FC38] hover:text-white transition-colors"
          >
            <HelpCircle className="w-3.5 h-3.5" /> How it works
          </button>
        </div>
        {hasPages && !formOpen && (
          <div className="flex items-center gap-2 shrink-0">
            {VISUAL_EDITOR_ENABLED && account?.siteUrl && (
              <Link
                to={visualPath}
                data-tour="visual-editor"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#B0FC38]/30 bg-[#B0FC38]/[0.08] text-sm text-white font-medium hover:bg-[#B0FC38]/[0.15] transition-all"
                title="Pick sections visually on your live site"
              >
                <MousePointerClick className="w-4 h-4 text-[#B0FC38]" />
                Visual editor
              </Link>
            )}
            <button
              type="button"
              data-tour="new-edit-request"
              onClick={() => setFormOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all"
            >
              <Plus className="w-4 h-4" />
              New edit request
            </button>
          </div>
        )}
        {formOpen && (
          <button
            type="button"
            onClick={() => setFormOpen(false)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all shrink-0"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        )}
      </div>

      <AnimatePresence>
        {justSubmitted && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="mb-5 flex items-center gap-3 px-4 py-3 rounded-xl bg-[#B0FC38]/[0.08] border border-[#B0FC38]/25"
          >
            <CheckCircle2 className="w-4 h-4 text-[#B0FC38] shrink-0" />
            <div className="text-sm text-gray-200 flex-1">
              Edit request submitted. NexSav will review and implement the change.
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loadError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
          {loadError}
        </div>
      )}

      {!loading && !hasPages && (
        <div className="mb-6 p-6 md:p-8 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#B0FC38]/[0.1] border border-[#B0FC38]/20 flex items-center justify-center shrink-0">
              <FileQuestion className="w-4 h-4 text-[#B0FC38]" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-white tracking-tight">
                No pages available yet
              </h3>
              <p className="text-[13px] text-gray-400 leading-relaxed mt-1.5 max-w-xl">
                NexSav hasn't scanned your site for editable pages yet. Reach out and we'll get it set up. It only takes a minute.
              </p>
              <a
                href="mailto:support@nexsav.net?subject=Enable%20content%20edit%20requests"
                className="inline-flex items-center gap-1.5 mt-4 text-[13px] text-[#B0FC38] hover:text-white transition-colors"
              >
                <Mail className="w-3.5 h-3.5" />
                Email support@nexsav.net
              </a>
            </div>
          </div>
        </div>
      )}

      <AnimatePresence initial={false}>
        {formOpen && hasPages && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="mb-6">
              <EditRequestForm pages={pages} onCreated={onCreated} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {websiteUpdates.length > 0 && (
        <WebsiteUpdates updates={websiteUpdates} siteOrigin={siteOrigin} onReverted={onReverted} />
      )}

      <EditRequestList editRequests={formRequests} loading={loading} onDelete={handleDelete} onEdit={handleEdit} viewer={viewer} />

      {showWalkthrough && !loading && hasPages && (
        <ContentEditsWalkthrough
          onClose={closeWalkthrough}
          onLaunchVisual={() => navigate(visualPath, { state: { tour: true } })}
          visualAvailable={visualAvailable}
        />
      )}
    </div>
  );
}

// "Website updates": what this school published from the Visual Editor.
// Each row opens a read-only record; the latest live one can be undone.
function WebsiteUpdates({ updates, siteOrigin, onReverted }) {
  const [openId, setOpenId] = useState(null);
  const [details, setDetails] = useState({}); // id → the full request (with its changes)

  const toggle = (id) => {
    const next = openId === id ? null : id;
    setOpenId(next);
    if (next && !details[next]) {
      Promise.resolve(api.get(`/api/edit-requests/${next}`))
        .then((d) => { if (d?.editRequest) setDetails((m) => ({ ...m, [next]: d.editRequest })); })
        .catch(() => { /* the summary row is still there */ });
    }
  };

  return (
    <section aria-labelledby="website-updates-heading" className="mb-6" data-testid="website-updates">
      <h2 id="website-updates-heading" className="text-sm font-medium text-white tracking-tight">Website updates</h2>
      <p className="text-[12px] text-gray-500 mt-0.5 mb-3">Changes you published yourself from the Visual editor.</p>
      <ul className="flex flex-col gap-2">
        {updates.map((r) => {
          const summary = summarizeWebsiteUpdate(details[r.id] || r);
          const isOpen = openId === r.id;
          const panelId = `website-update-${r.id}`;
          return (
            <li key={r.id} className="rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid="website-update-row">
              <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                <button type="button" onClick={() => toggle(r.id)} aria-expanded={isOpen} aria-controls={panelId}
                  className="flex-1 min-w-[220px] flex flex-wrap items-center gap-x-3 gap-y-1 text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/60">
                  <span className="text-[13px] text-white">{formatWhen(r.createdAt) || 'Recently'}</span>
                  <span className="text-[12px] text-gray-400 font-mono truncate max-w-[16rem]">{pagesText(summary)}</span>
                  {summary.changeCount != null && <span className="text-[12px] text-gray-400">{pluralize(summary.changeCount, 'change')}</span>}
                  <WebsiteUpdateStatusChip editRequest={r} />
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-500 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                  <span className="sr-only">{isOpen ? 'Hide details' : 'Show details'}</span>
                </button>
                {r.canRevert && (
                  <InlineUndoButton requestId={r.id} onReverted={onReverted} className="shrink-0" />
                )}
              </div>
              {isOpen && (
                <div id={panelId} className="px-4 pb-4 pt-1 border-t border-white/[0.05]">
                  <WebsiteUpdateRecord
                    editRequest={details[r.id]
                      // The row's state wins: it follows an undo made here.
                      ? { ...r, ...details[r.id], publishState: r.publishState, revertedAt: r.revertedAt, status: r.status }
                      : r}
                    mode="client"
                    siteOrigin={siteOrigin}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
